import React from "react";
import { Link } from "react-router-dom";
import background from "../../images/bg/ecosystem-light-background.png";
import artwork from "../../images/bg/ecosystem-art-reference.jpg";
import "./TrustedEngineeringEcosystem.css";

interface EcosystemItem {
  label: string;
  description: string;
  to: string;
  x: number;
  y: number;
}

const technologyItems: EcosystemItem[] = [
  { label: "AI & High-Performance Compute", description: "Compute architectures for next-generation applications.", to: "/industries", x: 65, y: 452 },
  { label: "Networking & Communication", description: "High-speed, low-latency connectivity solutions.", to: "/services/networking-solutions", x: 324, y: 452 },
  { label: "Storage & Memory", description: "Scalable memory and storage systems.", to: "/industries", x: 570, y: 449 },
  { label: "Automotive Electronics", description: "Functional safety and automotive-grade designs.", to: "/industries", x: 65, y: 654 },
  { label: "Defence & Aerospace", description: "High-reliability mission-critical systems.", to: "/industries", x: 324, y: 654 },
  { label: "Consumer & Industrial Electronics", description: "Innovative solutions for global markets.", to: "/industries", x: 570, y: 654 },
];

const methodologyItems: EcosystemItem[] = [
  { label: "RTL Design", description: "Scalable and lint-clean RTL.", to: "/services/asic-soc-design", x: 841, y: 453 },
  { label: "Functional Verification", description: "UVM, coverage and assertion-based.", to: "/services/verification", x: 1025, y: 453 },
  { label: "Physical Design", description: "Advanced node implementation.", to: "/services/physical-design", x: 1210, y: 453 },
  { label: "DFT", description: "Test planning and insertion.", to: "/services/dft", x: 1397, y: 453 },
  { label: "Static Timing Analysis (STA)", description: "Timing closure and sign-off.", to: "/services/static-timing-analysis", x: 841, y: 653 },
  { label: "Physical Verification", description: "DRC, LVS, ERC and reliability.", to: "/services/physical-design", x: 1025, y: 653 },
  { label: "Analog Design", description: "High-performance mixed-signal.", to: "/services/analog-mixed-signal", x: 1210, y: 653 },
  { label: "Silicon Realization", description: "End-to-end tapeout support.", to: "/services/silicon-realization", x: 1397, y: 653 },
];

const Arrow: React.FC = () => <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>;

const EcosystemCards: React.FC<{ items: EcosystemItem[]; variant: string }> = ({ items, variant }) => (
  <ul className={`tee-dark-grid tee-dark-grid--${variant}`}>
    {items.map(item => <li key={item.label}>
      <Link className="tee-dark-card" to={item.to}>
        <span
          className="tee-dark-card__art"
          aria-hidden="true"
          style={{
            backgroundImage: `url(${artwork})`,
            backgroundPosition: `${item.x / (1600 - 180) * 100}% ${item.y / (900 - 100) * 100}%`,
          }}
        />
        <h4>{item.label}</h4>
        <div className="tee-dark-card__bottom"><p>{item.description}</p><span className="tee-dark-card__arrow"><Arrow /></span></div>
      </Link>
    </li>)}
  </ul>
);

const TrustedEngineeringEcosystem: React.FC = () => (
  <section className="tee-dark-section" id="trusted-engineering-ecosystem" aria-labelledby="tee-dark-title" style={{ "--ecosystem-background": `url(${background})` } as React.CSSProperties}>
    <div className="tee-dark-container">
      <header className="tee-dark-intro">
        <p className="tee-dark-kicker">ENGINEERING ECOSYSTEM <span aria-hidden="true" /></p>
        <h2 id="tee-dark-title">Trusted Engineering<br /><span>Ecosystem</span></h2>
        <p className="tee-dark-copy">Digicomm Semiconductor combines industry-standard design environments, proven methodologies and collaborative engineering practices to deliver reliable silicon solutions.</p>
      </header>
      <div className="tee-dark-panels">
        <article className="tee-dark-panel tee-dark-panel--technology" aria-labelledby="tee-technology-title">
          <h3 id="tee-technology-title">Technology Ecosystem</h3>
          <p className="tee-dark-panel__copy">A comprehensive technology foundation spanning compute, connectivity and domain-specific solutions.</p>
          <EcosystemCards items={technologyItems} variant="technology" />
        </article>
        <article className="tee-dark-panel tee-dark-panel--methodology" aria-labelledby="tee-methodology-title">
          <h3 id="tee-methodology-title">Engineering Methodologies</h3>
          <p className="tee-dark-panel__copy">Proven methodologies and disciplined execution drive high-quality, first-time-right silicon.</p>
          <EcosystemCards items={methodologyItems} variant="methodology" />
        </article>
      </div>
    </div>
  </section>
);

export default TrustedEngineeringEcosystem;

